// Gebetszeiten: Typen (Spiegel von apps/core/src/contracts/prayer.ts) und Abfragen.
import { useQueries } from '@tanstack/react-query';
import type { LayoutElement, PrayerTable } from '@disploraq/layouts';
import { useMemo } from 'react';
import { api, ApiError } from './client';
import { useMe, useTenantQuery } from './queries';

export interface PrayerTimetable {
  id: string; name: string; fileName: string | null; firstDate: string; lastDate: string; dayCount: number; hasIqama: boolean;
  rowVersion: number; updatedAt: string;
}
export interface PrayerDay { date: string; times: string[]; iqama?: string[] }
export interface ImportReport {
  ok: boolean; dayCount: number; firstDate: string | null; lastDate: string | null; hasIqama: boolean;
  errors: { row: number; column?: string; message: string }[]; warnings: string[]; columns: { header: string; meaning: string }[];
  preview: PrayerDay[]; timetable?: PrayerTimetable;
}
export interface PrayerProvider {
  key: string; name: { de: string; en: string }; description: { de: string; en: string }; attribution: string; timezone: string; enabled: boolean;
  locations: { id: string; name: string }[];
}

export const qkPrayer = {
  tables: ['prayer-tables'] as const,
  providers: ['prayer-providers'] as const,
  data: (ref: string) => ['prayer-data', ref] as const,
};

export const usePrayerTables = () => useTenantQuery(qkPrayer.tables, async () => (await api.get<{ items: PrayerTimetable[] }>('/prayer/tables')).items);
export const usePrayerProviders = () => useTenantQuery(qkPrayer.providers, async () => (await api.get<{ items: PrayerProvider[] }>('/prayer/providers')).items);

/** Datei → Base64 (ohne data:-Präfix). */
export function fileBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error ?? new Error('Datei nicht lesbar'));
    r.readAsDataURL(file);
  });
}

/** Zeitplan hochladen bzw. ersetzen (dryRun = nur prüfen). Fehler der Datei kommen als Bericht zurück, nicht als Ausnahme. */
export async function uploadTimetable(file: File, name: string, opts: { id?: string; dryRun?: boolean } = {}): Promise<ImportReport> {
  const body = { name, fileName: file.name, contentBase64: await fileBase64(file), dryRun: !!opts.dryRun };
  try {
    return opts.id ? await api.put<ImportReport>(`/prayer/tables/${opts.id}`, body) : await api.post<ImportReport>('/prayer/tables', body);
  } catch (e) {
    const p = e instanceof ApiError ? (e.problem as unknown as Partial<ImportReport>) : null;
    if (p && Array.isArray(p.errors) && Array.isArray(p.columns)) return { ...(p as ImportReport), ok: false };
    throw e;
  }
}

/** Pfad der Daten eines Bausteins „Gebetszeiten" (eigener Zeitplan bzw. Anbieter) oder null bei Berechnung. */
export function prayerDataPath(el: Record<string, unknown>): string | null {
  if (el.source === 'table' && typeof el.table === 'string' && el.table) return `/prayer/tables/${el.table}/data`;
  if (el.source === 'provider' && typeof el.provider === 'string' && typeof el.location === 'string' && el.provider && el.location)
    return `/prayer/providers/${el.provider}/${el.location}/data`;
  return null;
}

/** Zeitpläne für die Vorschau im Studio (wie im Manifest des Bildschirms). */
export function usePrayerPreviewTables(elements: readonly LayoutElement[] | undefined): PrayerTable[] {
  const { data: me } = useMe();
  const paths = [...new Set((elements ?? []).filter((e) => e.type === 'prayer').map((e) => prayerDataPath(e as unknown as Record<string, unknown>)).filter((p): p is string => !!p))].sort();
  const res = useQueries({
    queries: paths.map((p) => ({
      queryKey: [...qkPrayer.data(p), me?.activeTenantId ?? 'none'],
      queryFn: () => api.get<PrayerTable>(p),
      staleTime: 10 * 60_000,
      retry: false,
      enabled: !!me?.activeTenantId,
    })),
  });
  const data = res.map((r) => r.data);
  const key = data.map((d) => (d ? `${d.id}:${d.days.length}` : '-')).join('|');
  return useMemo(() => data.filter((d): d is PrayerTable => !!d), [key]);
}
